import { api } from "@lib/orpc-client";
import * as Sentry from "@sentry/react";
import { useCallback, useEffect, useState } from "react";

export type AddAddressParams = {
  name: string;
  address: string;
};

export type EditAddressParams = AddAddressParams & {
  uuid: string;
};

export type RemoveAddressParams = {
  uuid: string;
};

export type Address = {
  address_uuid: string;
  name: string;
  address: string;
  address_tag?: string;
  network: string;
  asset: string;
};

const useAddressBookManagement = (fetchAddresses: boolean = true) => {
  const addAddress = async ({ name, address }: AddAddressParams) => {
    try {
      return await api.wallet.address.add({ name, address });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const editAddress = async (params: EditAddressParams) => {
    try {
      return await api.wallet.address.edit({
        address_uuid: params.uuid,
        name: params.name,
        address: params.address,
      });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const removeAddress = async (params: RemoveAddressParams) => {
    try {
      return await api.wallet.address.delete({ address_uuid: params.uuid });
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    }
  };

  const [addresses, setAddresses] = useState<Address[]>([]);
  const [localLoading, setLocalLoading] = useState(true);

  const getDataBase = async () => {
    try {
      setLocalLoading(true);
      const addresses = (await api.wallet.address.list({})) as Address[];
      setAddresses(addresses);
    } catch (error) {
      Sentry.captureException(error);
      throw error;
    } finally {
      setLocalLoading(false);
    }
  };

  const getData = useCallback(async () => {
    await getDataBase();
  }, []);

  useEffect(() => {
    if (!fetchAddresses) {
      setLocalLoading(false);
      return;
    }

    void getData().catch(() => undefined);
  }, [fetchAddresses, getData]);

  return {
    addAddress,
    editAddress,
    removeAddress,
    addresses: addresses as Address[],
    isLoading: localLoading,
    reload: getDataBase,
  };
};

export default useAddressBookManagement;
